import Link from "next/link";
import { useRouter } from "next/router";
import { FormEvent, useEffect, useState } from "react";

import { Button, Card, Dropdown, Skeleton } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import { AppShell } from "@chef/feature-app-shell";
import { formatDate, isMealboxBasketType } from "@chef/helpers";
import {
  CalendarQuery,
  ICreateOrUpdateMultipleSchedulersArgs,
  useCreateOrUpdateMultipleSchedulersMutation,
} from "@chef/state-management";
import { getWeeklyTimeblockDataFromCalendar } from "@chef/state-management/helpers";
import { useTrack } from "@chef/state-management/hooks";

import { useMyAccountCalendarQuery } from "../../../hooks/useMyAccountCalendarQuery";
import { DeliveryCutoffBanner } from "../../../components/DeliveryCutoffBanner";
import { useTrackingData } from "../../../hooks/useTrackingData";

import { intl } from "./PauseSubscriptionForPeriodPage.Intl";

export const PauseSubscriptionForPeriodPage = () => {
  const router = useRouter();

  const track = useTrack();
  const trackingData = useTrackingData();

  useEffect(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Pause subscription for period",
      url: window.location.href,
    });
  }, [track]);

  const [fromWeekIndex, setFromWeekIndex] = useState<number>();
  const [toWeekIndex, setToWeekIndex] = useState<number>();

  const { data: { calendar } = {}, isLoading: isLoadingCalendar } =
    useMyAccountCalendarQuery();

  const [createOrUpdateMultipleSchedulers, { isLoading: isSubmitting }] =
    useCreateOrUpdateMultipleSchedulersMutation();

  const fromWeekOptions = createWeekOptions(calendar);

  const toWeekOptions = [...fromWeekOptions];
  toWeekOptions.splice(0, fromWeekIndex || 0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!calendar || fromWeekIndex === undefined || toWeekIndex === undefined) {
      return;
    }

    const data: ICreateOrUpdateMultipleSchedulersArgs["schedulers"] = [];

    for (let i = fromWeekIndex; i <= toWeekIndex; i++) {
      data.push({
        delivery: false,
        week: calendar[i].week,
        year: calendar[i].year,
      });
    }

    try {
      await createOrUpdateMultipleSchedulers({ schedulers: data });

      const fromTimeblock = getWeeklyTimeblockDataFromCalendar({
        calendar,
        week: calendar[fromWeekIndex].week,
        year: calendar[fromWeekIndex].year,
      });

      const toTimeblock = getWeeklyTimeblockDataFromCalendar({
        calendar,
        week: calendar[toWeekIndex].week,
        year: calendar[toWeekIndex].year,
      });

      track("skipMultipleWeeks", {
        affiliation: "Frontend process",
        interval__end_year: calendar[toWeekIndex].year,
        interval_end_week: calendar[toWeekIndex].week,
        interval_start_year: calendar[fromWeekIndex].year,
        interval_start_week: calendar[fromWeekIndex].week,
        number_of_weeks: toWeekIndex - fromWeekIndex + 1,
        products: trackingData?.products,
        status: trackingData?.status,
        timeblock_day: trackingData?.timeblock_day,
        timeblock_id: trackingData?.timeblock_id,
        timeblock_interval: trackingData?.timeblock_interval,
        url: window.location.href,
      });

      track("updateSubscriptionStepCompleted", {
        affiliation: "Frontend process",
        changed_subscription_details: "Pause subscription for period",
        url: window.location.href,
      });

      const url = `${BRAND_PATHS.PAUSE_FOR_PERIOD_SUCCESS_PATH}?fromDate=${fromTimeblock?.deliveryDate}&toDate=${toTimeblock?.deliveryDate}`;

      router.push(url);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <DeliveryCutoffBanner />

      {/* Appshell.Main added inside feature lib as it was deemed the least hacky way of implementing the gradient bg */}
      <AppShell.Main grid noPadding gradient>
        <Card
          noPadding
          className="h-full px-4 py-6 col-span-full lg:col-span-6 lg:col-start-2 xl:col-span-4 xl:col-start-5 lg:p-10 lg:h-auto lg:mt-20"
        >
          <form onSubmit={handleSubmit} className="flex flex-col">
            <h2 className="text-xl text-center">
              <strong>{intl.PAUSE_FOR_A_LIMITED_PERIOD}</strong>
            </h2>

            <div className="flex flex-col gap-6 mt-6">
              {isLoadingCalendar && (
                <>
                  <div className="flex flex-col gap-2">
                    <Skeleton width={20} />
                    <Skeleton height={10} />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Skeleton width={20} />
                    <Skeleton height={10} />
                  </div>
                </>
              )}

              {!isLoadingCalendar && (
                <>
                  <div>
                    <label className="text-sm">
                      <strong>{intl.FROM_DATE}:</strong>
                    </label>

                    <Dropdown
                      className="mt-1"
                      options={fromWeekOptions}
                      onChange={setFromWeekIndex}
                      value={
                        fromWeekIndex && fromWeekOptions[fromWeekIndex].value
                      }
                      placeholder={intl.SELECT_DATE}
                    />
                  </div>

                  <div>
                    <label className="text-sm">
                      <strong>{intl.TO_DATE}:</strong>
                    </label>

                    <Dropdown
                      className="mt-1"
                      options={toWeekOptions}
                      onChange={setToWeekIndex}
                      value={toWeekIndex && fromWeekOptions[toWeekIndex].value}
                      placeholder={intl.SELECT_DATE}
                    />
                  </div>
                </>
              )}

              <p className="p-4 text-sm bg-grey-2">
                {intl.YOU_WILL_NOT_RECEIVE_ANY_DELIVERY_IN_THIS_PERIOD}
              </p>
            </div>

            <Button
              primary
              type="submit"
              className="mt-10 lg:self-center lg:mt-6"
              loading={isSubmitting}
              disabled={
                isLoadingCalendar ||
                fromWeekIndex === undefined ||
                toWeekIndex === undefined
              }
            >
              {intl.REGISTER_PAUSE}
            </Button>

            <Link
              href={BRAND_PATHS.MY_SUBSCRIPTION_LINK}
              passHref
              className="self-center mt-10 underline lg:mt-6 text-primary"
            >
              {intl.CANCEL}
            </Link>
          </form>
        </Card>
      </AppShell.Main>
    </>
  );
};

const createWeekOptions = (calendar?: CalendarQuery["calendar"]) => {
  return (
    calendar
      ?.filter((calendarWeek) => calendarWeek.baskets.find(isMealboxBasketType))
      .map((calendarWeek, index) => {
        const timeblockId =
          calendarWeek.baskets.find(isMealboxBasketType)?.shippingDetails
            .timeblockId;

        const weeklyDeliveryTimeblock = calendarWeek.baskets
          .find((basket) => basket.isDefault)
          ?.shippingDetails.addressInfo.weeklyTimeblocks.find(
            (timeblock) => timeblock.timeblockId === timeblockId,
          );

        const label = weeklyDeliveryTimeblock?.deliveryDate
          ? formatDate(weeklyDeliveryTimeblock.deliveryDate, "EEEE d/LL-yyyy")
          : "No delivery";

        return {
          label,
          value: index,
        };
      }) || []
  );
};
